import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Cookies } from 'react-cookie';
import { API_PATH } from '../constants';
import { ApiResult } from './types';
import { toApiErrorResult } from './errors';

const TOKEN_COOKIE_NAME = 'pgAdminToken';
const cookies = new Cookies();

function readToken(): string | undefined {
    return cookies.get(TOKEN_COOKIE_NAME);
}

const instance: AxiosInstance = axios.create({
    baseURL: API_PATH,
    headers: { 'Content-Type': 'application/json' }
});

instance.interceptors.request.use((config) => {
    const token = readToken();
    if (token) {
        config.headers = config.headers ?? ({} as any);
        // Backend currently expects the raw token (no `Bearer ` prefix).
        // Update to `Bearer ${token}` once backend ships PGA3 (see Newnex-Backend/ISSUES-TRACKER.md).
        (config.headers as any).Authorization = token;
    }
    return config;
});

const isBrowser =
    typeof window !== 'undefined' && typeof document !== 'undefined';

instance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401 && isBrowser) {
            cookies.remove(TOKEN_COOKIE_NAME, { path: '/' });
            // Force App.tsx to re-render Login (it gates on cookie presence).
            window.location.reload();
        }
        return Promise.reject(error);
    }
);

export const apiClient = instance;

export async function call<T = any>(
    config: AxiosRequestConfig
): Promise<ApiResult<T>> {
    try {
        const response: AxiosResponse<{ data: T }> = await instance(config);
        return { type: 'success', response: response?.data?.data as T };
    } catch (error) {
        return toApiErrorResult(error);
    }
}

export function get<T = any>(
    url: string,
    config?: AxiosRequestConfig
): Promise<ApiResult<T>> {
    return call<T>({ ...config, method: 'get', url });
}

export function post<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
): Promise<ApiResult<T>> {
    return call<T>({ ...config, method: 'post', url, data });
}

export function put<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
): Promise<ApiResult<T>> {
    return call<T>({ ...config, method: 'put', url, data });
}

export function patch<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
): Promise<ApiResult<T>> {
    return call<T>({ ...config, method: 'patch', url, data });
}

export function del<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
): Promise<ApiResult<T>> {
    return call<T>({ ...config, method: 'delete', url, data });
}
